/**
 * Admin API client. Each function fetches the Worker `/admin/*`
 * with `Authorization: Bearer <idToken>`. The Worker enforces the admin claim and
 * returns ONLY masked previews + non-secret config (never raw keys).
 *
 * `fetchImpl` is injectable for tests. Responses are typed; non-2xx throws an
 * `AdminApiError` with a generic message (Worker bodies are already opaque).
 *
 * Domain: AutoDieta only. Nutrition/diet domain exclusively — no fitness plan domain.
 */

import type { ProviderId } from "@autodieta/shared";

export interface AdminApiOptions {
  /** Worker API base URL (no trailing slash needed). */
  workerUrl: string;
  /** Fresh Firebase ID token (admin claim required server-side). */
  idToken: string;
  /** Injectable fetch for tests. Defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

export interface ProviderConfig {
  configured: boolean;
  masked: string;
  model: string;
}

export interface AiConfigView {
  activeProvider: ProviderId;
  providers: Record<ProviderId, ProviderConfig>;
}

export interface ModelInfo {
  id: string;
  display_name: string;
}

export interface AdminUserSummary {
  uid: string;
  email: string;
  displayName: string;
  createdAt: string;
  lastLoginAt?: string;
  loginCount?: number;
  authProvider?: string;
  banned?: boolean;
}

export interface AdminChatSummary {
  id: string;
  role: string;
  preview: string;
  createdAt: string;
}

export interface AdminUserDetail {
  profile: AdminUserSummary | null;
  recentChat: AdminChatSummary[];
}

export interface AdminAuditEntry {
  id: string;
  source: "jailbreak" | "config";
  uid?: string;
  kind: string;
  snippet?: string;
  createdAt: string;
}

export interface AdminProfilePatch {
  displayName?: string;
  banned?: boolean;
}

export class AdminApiError extends Error {
  override name = "AdminApiError";
  readonly status: number;
  constructor(message: string, status = 0) {
    super(message);
    this.status = status;
  }
}

function base(opts: AdminApiOptions): string {
  return opts.workerUrl.replace(/\/+$/, "");
}

async function request<T>(
  opts: AdminApiOptions,
  path: string,
  method: "GET" | "POST" | "PATCH" | "DELETE",
  body?: unknown,
): Promise<T> {
  const fetchImpl = opts.fetchImpl ?? fetch;
  const headers: Record<string, string> = { Authorization: `Bearer ${opts.idToken}` };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let res: Response;
  try {
    res = await fetchImpl(`${base(opts)}${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new AdminApiError("Request failed.");
  }
  if (!res.ok) {
    throw new AdminApiError("Request was rejected.", res.status);
  }
  try {
    return (await res.json()) as T;
  } catch {
    throw new AdminApiError("Invalid response.");
  }
}

// ── AI configuration ──────────────────────────────────────────────────────────

export function aiGetConfig(opts: AdminApiOptions): Promise<AiConfigView> {
  return request<AiConfigView>(opts, "/admin/ai/config", "GET");
}

export function aiSetKey(
  opts: AdminApiOptions,
  provider: ProviderId,
  key: string,
): Promise<{ ok: boolean; masked: string }> {
  return request(opts, "/admin/ai/setKey", "POST", { provider, key });
}

export function aiSetActive(
  opts: AdminApiOptions,
  provider: ProviderId,
): Promise<{ ok: boolean; activeProvider: ProviderId }> {
  return request(opts, "/admin/ai/setActive", "POST", { provider });
}

export function aiSetModel(
  opts: AdminApiOptions,
  provider: ProviderId,
  model: string,
): Promise<{ ok: boolean; model: string }> {
  return request(opts, "/admin/ai/setModel", "POST", { provider, model });
}

export function aiTestProvider(
  opts: AdminApiOptions,
  provider: ProviderId,
): Promise<{ ok: boolean; message: string }> {
  return request(opts, "/admin/ai/testProvider", "POST", { provider });
}

export function aiListModels(
  opts: AdminApiOptions,
  provider: ProviderId,
  forceRefresh = false,
): Promise<{ models: ModelInfo[] }> {
  return request(opts, "/admin/ai/listModels", "POST", { provider, forceRefresh });
}

// ── User oversight ─────────────────────────────────────────────────────────────

export async function adminListUsers(opts: AdminApiOptions): Promise<AdminUserSummary[]> {
  // Worker responds with { users: [...] }. Unwrap and guard so an unexpected
  // shape can never crash the panel with `.map is not a function`.
  const data = await request<{ users?: AdminUserSummary[] }>(opts, "/admin/users", "GET");
  return Array.isArray(data?.users) ? data.users : [];
}

export function adminGetUser(opts: AdminApiOptions, uid: string): Promise<AdminUserDetail> {
  return request<AdminUserDetail>(opts, `/admin/users/${encodeURIComponent(uid)}`, "GET");
}

// ── Audit ────────────────────────────────────────────────────────────────────

export interface AdminAuditListResponse {
  entries: AdminAuditEntry[];
}

export async function adminGetAudit(opts: AdminApiOptions): Promise<AdminAuditListResponse> {
  // Worker responds with { records: [...] }; the panel expects { entries }.
  // Map + guard so an unexpected shape never crashes the panel.
  const data = await request<{ records?: AdminAuditEntry[] }>(opts, "/admin/audit", "GET");
  return { entries: Array.isArray(data?.records) ? data.records : [] };
}
